import {
    FleetObject,
    FleetObjectState,
    ProductHeader,
    useGetProductFleetQuery,
} from '../../api/product/productObjectsApiSlice';
import { formatAddressLine } from '../../utils/addressLine';

// ─────────────────────────────────────────────────────────────────────────────
// The W1.2a seam.
//
// This file is the ONE place that knows how a product's objects are found and
// how their live state is read. Both halves move together when the target
// model lands, so they live together — a seam that covered only the state
// would still leave the enumeration to fix in a second place.
//
// Today (observed, not assumed):
//   * A "product" is a `Group`. `Product` and `product_resource_link` are in
//     no Prisma schema yet — checked across all six schema files — so a
//     product id IS a group id, and the fleet is `asset WHERE group_id = …`.
//   * A product's objects are read through `GET /asset/group/:groupId`, which
//     applies `liveHolderRentalItemWhere()` server-side and returns the
//     reconciled state plus the holding booking.
//
// After W1.2a:
//   * `productIdToGroupId` disappears; the id is a real `product_id`.
//   * The backend read swaps its where-clause to `product_resource_link`.
//   * `mode` (`any_of` / `exclusive`, architecture §3) becomes available and
//     belongs on `FleetObject`.
//   * Per-object *future* occupancy becomes answerable for the first time
//     (`reservationitem.asset_id`, architecture §6). Until then it does not
//     exist for a pool — the object is chosen LRU at rental start — which is
//     why the fleet shows three states and no "soon".
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A product id, as the UI passes it around.
 *
 * Nominally distinct from a group id so the cutover is a compile-time task
 * rather than a search for string variables named `id`.
 */
export type ProductId = string;

/**
 * Resolve a product id to the group that backs it.
 *
 * The identity function today, and deliberately not inlined: it is the marker
 * for every place that still assumes product ≡ group.
 */
export const productIdToGroupId = (productId: ProductId): string => productId;

/** Display order: what needs attention first, then what is simply busy. */
const STATE_ORDER: Record<FleetObjectState, number> = {
    blocked: 0,
    inUse: 1,
    available: 2,
};

/**
 * Badge styling per state — tenant primary and grayscale only (CLAUDE.md).
 * `inUse` takes the accent because it is the live state; `blocked` inverts to
 * read as deliberately marked without reaching for red.
 */
export const FLEET_STATE_BADGE: Record<FleetObjectState, string> = {
    available: 'bg-foreground/5 text-foreground/70',
    inUse: 'bg-primary/10 text-primary',
    blocked: 'bg-foreground/80 text-text-contrast',
};

/** i18n key for a state, so the vocabulary is defined once. */
export const fleetStateLabelKey = (state: FleetObjectState): string =>
    `product_detail.objects.state.${state}`;

/**
 * One-line postal address of the station an object sits at.
 *
 * The rule itself — including the house-number de-duplication this surface was
 * the first to need — now lives in `utils/addressLine.ts`, because three other
 * screens were answering the same question without the house number at all
 * (QA #2439, defect D). This keeps only the fleet's own contract: `null` rather
 * than an empty string, so the caller can leave the row out entirely.
 */
export const formatFleetAddress = (object: FleetObject): string | null =>
    formatAddressLine(object.location?.address) || null;

/**
 * Why the fleet failed to load, when it did.
 *
 * One "could not be loaded" for every cause makes the reader do the
 * diagnosis, and the two most likely causes here need opposite responses:
 * a **404** during a deploy window is expected and resolves itself once the
 * backend rolls out, while **no response at all** is a misconfigured API
 * address or a down service and resolves itself never. Rendering them
 * identically is the #1579/#1581 pattern one layer down.
 *
 * Both base queries return `{ status, data }` with `status` **undefined**
 * when no response came back (`utils/axiosBaseQuery.ts`), so the two are
 * already distinguishable without adding anything to the request path.
 * Raised by the utilization session, which made the same split in its own
 * section — the wording matches theirs deliberately, since both sections
 * render on this page.
 */
export type FleetErrorKind = 'not_found' | 'unreachable' | 'failed';

export interface FleetError {
    kind: FleetErrorKind;
    /** HTTP status, when there was a response at all. */
    status?: number;
}

const classifyFleetError = (error: unknown): FleetError => {
    const status = (error as { status?: unknown } | undefined)?.status;
    if (typeof status !== 'number') return { kind: 'unreachable' };
    if (status === 404) return { kind: 'not_found', status };
    return { kind: 'failed', status };
};

export interface ProductFleet {
    product: ProductHeader | null;
    objects: FleetObject[];
    isLoading: boolean;
    isError: boolean;
    /** Present only when `isError`. */
    error: FleetError | null;
}

/**
 * The objects fulfilling one product, ordered for the table.
 *
 * `isError` is surfaced rather than folded into an empty list: a product with
 * no objects and a product whose fleet failed to load are different facts, and
 * rendering the second as the first is how a broken read stays invisible
 * (#1579/#1581).
 */
export const useProductFleet = (productId: ProductId | undefined): ProductFleet => {
    const { data, isLoading, isError, error } = useGetProductFleetQuery(
        { groupId: productIdToGroupId(productId ?? '') },
        { skip: !productId },
    );

    const objects = [...(data?.objects ?? [])].sort((a, b) => {
        const byState = STATE_ORDER[a.state] - STATE_ORDER[b.state];
        if (byState !== 0) return byState;
        const aDoor = a.doors[0];
        const bDoor = b.doors[0];
        if (aDoor != null && bDoor != null && aDoor !== bDoor) return aDoor - bDoor;
        return a.name.localeCompare(b.name);
    });

    return {
        product: data?.product ?? null,
        objects,
        isLoading,
        isError,
        error: isError ? classifyFleetError(error) : null,
    };
};
